import { Track } from 'livekit-client';
import React from 'react';
import {
  ChatIcon,
  ChatToggle,
  DisconnectButton,
  GearIcon,
  LeaveIcon,
  //MediaDeviceMenu,
  StartMediaButton,
  //TrackToggle,
  useTrackVolume,
  useLocalParticipantPermissions,
  useMaybeLayoutContext,
  usePersistentUserChoices,
  BarVisualizer,
  useLocalParticipant,
} from '@livekit/components-react';
import { mergeProps, useMediaQuery } from '../../../helpers/utilities';
import { supportsScreenSharing } from '@livekit/components-core';
import { SettingsMenuToggle } from './SettingsMenuToggle';
import { TrackToggle } from '../../LiveKit/TrackToggle';
import { MediaDeviceMenu } from '../../LiveKit/MediaDeviceMenu';
import { Flex } from '@smarterservices/smarter-ui';
import { getStore } from '../../../stores/StoreContext';
import AudioVisualizer from './AudioVisualizer';
import {
  applyNoiseSuppressionToTrack,
  stopAudioProcessing,
} from '../../../helpers/audioProcessing';

export function ControlBar({
  variation,
  controls,
  saveUserChoices = true,
  onDeviceError,
  ...props
}) {
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [audioTrack, setAudioTrack] = React.useState(null);

  const layoutContext = useMaybeLayoutContext();
  const browserSupportsScreenSharing = supportsScreenSharing();
  const localPermissions = useLocalParticipantPermissions();
  const { localParticipant } = useLocalParticipant();

  const AppStore = getStore('appStore');
  //const audioLevel = useAudioLevel(audioTrack);
  //console.log(audioLevel);

  React.useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext?.widget.state?.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);
  const isTooLittleSpace = useMediaQuery(
    `(max-width: ${isChatOpen ? 1000 : 760}px)`
  );

  const defaultVariation = isTooLittleSpace ? 'minimal' : 'verbose';
  variation ??= defaultVariation;

  if (!localPermissions) {
    controls.camera = false;
    controls.chat = false;
    controls.microphone = false;
    controls.screenShare = false;
  } else {
    // Enable each control menue button by if the user has permissions, and whether it was passed in on the
    // enabledComponents prop
    controls.camera ??= localPermissions.canPublish && controls?.camera;
    controls.microphone ??= localPermissions.canPublish && controls?.microphone;
    controls.screenShare ??=
      localPermissions.canPublish && controls?.screenShare;
    controls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const showIcon = React.useMemo(
    () => variation === 'minimal' || variation === 'verbose',
    [variation]
  );
  const showText = React.useMemo(
    () => variation === 'textOnly' || variation === 'verbose',
    [variation]
  );

  const htmlProps = mergeProps({ className: 'lk-control-bar' }, props);

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const onMicrophoneChange = React.useCallback(
    (enabled, isUserInitiated) => {
      AppStore.setIsMicrophoneShareActive(enabled);
      isUserInitiated ? saveAudioInputEnabled(enabled) : null;

      if (
        localParticipant &&
        localParticipant.audioTrackPublications &&
        localParticipant.audioTrackPublications?.size > 0
      ) {
        const [[trackSid, publication]] = Array.from(
          localParticipant.audioTrackPublications.entries()
        );
        // Apply noise suppression when microphone is enabled
        if (enabled && publication.track) {
          applyNoiseSuppressionToTrack(publication.track)
            .then(() => console.log('Noise suppression applied'))
            .catch((err) =>
              console.error('Error applying noise suppression:', err)
            );
        } else if (!enabled && publication.track) {
          // Clean up audio processing when microphone is disabled
          stopAudioProcessing(publication.track);
        }
        setAudioTrack(publication.track);
      }
    },
    [saveAudioInputEnabled, applyNoiseSuppressionToTrack, stopAudioProcessing]
  );

  const onCameraChange = React.useCallback(
    (enabled, isUserInitiated) => {
      AppStore.setIsCameraShareActive(enabled);
      isUserInitiated ? saveVideoInputEnabled(enabled) : null;
    },
    [saveVideoInputEnabled]
  );

  const onScreenShareChange = React.useCallback(
    (enabled) => {
      AppStore.setIsScreenShareActive(enabled);
    },
    [AppStore.setIsScreenShareActive]
  );

  return (
    <Flex>
      {controls.microphone && (
        <Flex>
          <Flex.Item>
            <TrackToggle
              source={Track.Source.Microphone}
              showIcon={showIcon}
              isEnabled={AppStore.isMicrophoneShareActive}
              onChange={onMicrophoneChange}
              onDeviceError={(error) =>
                onDeviceError?.({ source: Track.Source.Microphone, error })
              }
            >
              {showText &&
                (AppStore.isMicrophoneShareActive
                  ? props.microphoneEnabledLabel || 'Microphone'
                  : props.microphoneDisabledLabel || 'Microphone')}
            </TrackToggle>
          </Flex.Item>
          <Flex.Item>
            <MediaDeviceMenu
              kind="audioinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveAudioInputDeviceId(deviceId ?? '')
              }
            />
          </Flex.Item>
          <Flex.Item>
            <AudioVisualizer track={audioTrack} barCount={3} />
          </Flex.Item>
        </Flex>
      )}
      {controls.camera && (
        <Flex>
          <Flex.Item>
            <TrackToggle
              source={Track.Source.Camera}
              showIcon={showIcon}
              isEnabled={AppStore.isCameraShareActive}
              onChange={onCameraChange}
              onDeviceError={(error) =>
                onDeviceError?.({ source: Track.Source.Camera, error })
              }
            >
              {showText &&
                (AppStore.isCameraShareActive
                  ? props.cameraEnabledLabel || 'Camera'
                  : props.cameraDisabledLabel || 'Camera')}
            </TrackToggle>
          </Flex.Item>
          <Flex.Item>
            <MediaDeviceMenu
              kind="videoinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveVideoInputDeviceId(deviceId ?? '')
              }
            />
          </Flex.Item>
        </Flex>
      )}
      {controls.screenShare && browserSupportsScreenSharing && (
        <TrackToggle
          source={Track.Source.ScreenShare}
          captureOptions={{ audio: false, selfBrowserSurface: 'include' }}
          showIcon={showIcon}
          isEnabled={AppStore.isScreenShareActive}
          onChange={onScreenShareChange}
          onDeviceError={(error) =>
            onDeviceError?.({ source: Track.Source.ScreenShare, error })
          }
        >
          {showText &&
            (AppStore.isScreenShareActive
              ? props.screenShareEnabledLabel || 'Stop screen share'
              : props.screenShareDisabledLabel || 'Share screen')}
        </TrackToggle>
      )}
      <StartMediaButton />
    </Flex>
  );
}
